import React, { useEffect, useState } from "react";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  ApiNotificationStatus,
  NotificationDetailDto,
  notificationApi,
} from "../../../../services/notificationApi";
import DeliveryFunnel from "./DeliveryFunnel";
import {
  formatNotificationDate,
  notificationStatus,
  notificationTarget,
} from "./notificationPresentation";
import styles from "./notifications.module.scss";

interface NotificationDetailsProps {
  id: string;
}

const NotificationDetails: React.FC<NotificationDetailsProps> = ({ id }) => {
  const navigate = useNavigate();
  const [notification, setNotification] = useState<NotificationDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setNotification(null);
    notificationApi.getById(id)
      .then((result) => {
        if (!cancelled) setNotification(result);
      })
      .catch(() => {
        if (!cancelled) {
          setNotification(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className={`${styles["notification-empty-state"]} ${styles["notification-empty-state--page"]}`} role="status">Loading notification…</div>;

  if (error || !notification) {
    return (
      <div className={`${styles["notification-empty-state"]} ${styles["notification-empty-state--page"]}`} role="alert">
        <strong>Notification unavailable</strong>
        <span>It could not be loaded from the notification API.</span>
        <button type="button" className={styles["notification-primary-button"]} onClick={() => navigate("/app/settings/notifications")}>Back to notifications</button>
      </div>
    );
  }

  const status = notificationStatus(notification.status);
  const date = formatNotificationDate(notification.sentAt || notification.scheduledAt || notification.createdDate);
  const dateVerb = notification.status === ApiNotificationStatus.Scheduled ? "scheduled" : notification.status === ApiNotificationStatus.Sent ? "sent" : "created";
  const metadata = [
    notificationTarget(notification.targetType),
    date !== "—" ? `${dateVerb} ${date}` : undefined,
  ].filter(Boolean);
  const metrics = {
    targeted: notification.totalDevices,
    sent: notification.sentCount,
    delivered: notification.deliveredCount,
    opened: notification.openedCount,
    failed: notification.failedCount,
  };
  const showMetrics = notification.status !== ApiNotificationStatus.Draft && notification.status !== ApiNotificationStatus.Scheduled;

  return (
    <div className={styles["notification-detail"]}>
      <div className={styles["notification-detail__heading-row"]}>
        <div>
          <button type="button" className={styles["notification-back-button"]} onClick={() => navigate("/app/settings/notifications")}>
            <FiArrowLeft aria-hidden="true" />
            <span>{notification.title || "Untitled notification"}</span>
          </button>
          <p className={styles["notification-detail__metadata"]}>{metadata.join(" · ")}</p>
        </div>
        <span className={`${styles["notification-status"]} ${styles[`notification-status--${status.className}`] || ""}`}>{status.label}</span>
      </div>

      {notification.status === ApiNotificationStatus.Draft && <div className={styles["notification-state-panel"]}><strong>This notification is a draft.</strong><span>Delivery analytics will appear after it is sent.</span></div>}
      {notification.status === ApiNotificationStatus.Scheduled && <div className={styles["notification-state-panel"]}><strong>This notification is scheduled.</strong><span>Delivery analytics will appear after the scheduled send is processed.</span></div>}

      {showMetrics && (
        <>
          <div className={styles["notification-metrics"]}>
            {([
              ["Targeted", metrics.targeted],
              ["Sent", metrics.sent],
              ["Delivered", metrics.delivered],
              ["Opened", metrics.opened],
              ["Failed", metrics.failed],
            ] as Array<[string, number]>).map(([label, value]) => (
              <div className={`${styles["notification-metric"]} ${label === "Failed" ? styles["notification-metric--failed"] : ""}`} key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
          <DeliveryFunnel metrics={metrics} />
          <section className={`${styles["notification-detail__section"]} ${styles["notification-failures"]}`}>
            <h3>Failure breakdown</h3>
            <div className={styles["notification-failures__empty"]}>Failure reasons are not provided by the notification API.</div>
          </section>
        </>
      )}
    </div>
  );
};

export default NotificationDetails;
